// src/pages/ResetPasswordPage.js
import { useState, useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { withBase } from '../config/api'
import Overlay from '../components/Overlay'
import showPwd from '../assets/images/icons/show_pwd.png'
import hidePwd from '../assets/images/icons/hide_pwd.png'
import {
  modalPrimaryButtonStyle,
  modalSecondaryButtonStyle
} from '../styles/buttonStyles'

const ResetPasswordPage = () => {
  const { token } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const langFromQuery = searchParams.get('lang')?.split('-')[0]?.toLowerCase()

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [message, setMessage] = useState(null)
  const [tokenValid, setTokenValid] = useState(null)
  const [isSuccess, setIsSuccess] = useState(false)

  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  const [hoverBtn, setHoverBtn] = useState(null)
  const [pressedBtn, setPressedBtn] = useState(null)

  const closeModal = () => {
    navigate('/', { replace: true })
  }

  useEffect(() => {
    if (!langFromQuery || i18n.language === langFromQuery) return

    i18n.changeLanguage(langFromQuery)
    localStorage.setItem('lang', langFromQuery)
  }, [i18n, langFromQuery])

  useEffect(() => {
    const validateToken = async () => {
      setIsLoading(true)
      setMessage(null)

      try {
        const res = await fetch(withBase('/auth/validate-reset-token'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept-Language': i18n.language
          },
          body: JSON.stringify({ token })
        })

        if (res.ok) {
          setTokenValid(true)
        } else {
          setTokenValid(false)
          setMessage({
            text: t('RESET-TOKEN-EXPIRED'),
            type: 'error'
          })
        }
      } catch {
        setTokenValid(false)
        setMessage({
          text: t('RESET_PASSWORD_ERROR'),
          type: 'error'
        })
      } finally {
        setIsLoading(false)
      }
    }

    if (!token) {
      setTokenValid(false)
      setMessage({
        text: t('RESET-TOKEN-EXPIRED'),
        type: 'error'
      })
      return
    }

    validateToken()
  }, [token, i18n.language, t])

  const validatePassword = (pwd) => {
    if (pwd.length < 10) {
      return t('PASSWORD_TOO_SHORT')
    }

    if (!/[A-Z]/.test(pwd)) {
      return t('PASSWORD_NEEDS_UPPER')
    }

    if (!/[0-9]/.test(pwd)) {
      return t('PASSWORD_NEEDS_NUMBER')
    }

    if (!/[!@#$%^&*(),.?":{}|<>_\-\\[\]/+=~`]/.test(pwd)) {
      return t('PASSWORD_NEEDS_SPECIAL')
    }

    return null
  }

  const submit = async (e) => {
    if (e) e.preventDefault()

    if (isSuccess) {
      navigate('/?showLogin=1', { replace: true })
      return
    }

    if (!password) {
      setMessage({
        text: t('PASSWORD_REQUIRED'),
        type: 'error'
      })
      return
    }

    if (!confirm) {
      setMessage({
        text: t('PASSWORD_CONFIRM_REQUIRED'),
        type: 'error'
      })
      return
    }

    const validationError = validatePassword(password)

    if (validationError) {
      setMessage({
        text: validationError,
        type: 'error'
      })
      return
    }

    if (password !== confirm) {
      setMessage({
        text: t('PASSWORD-MISMATCH'),
        type: 'error'
      })
      return
    }

    setIsLoading(true)
    setMessage(null)

    try {
      const res = await fetch(withBase('/auth/reset-password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': i18n.language
        },
        body: JSON.stringify({
          token,
          newPassword: password
        })
      })

      const data = await res.json()

      if (res.ok) {
        setMessage({
          text: t('RESET_PASSWORD_SUCCESS'),
          type: 'success'
        })

        setIsSuccess(true)
      } else {
        setMessage({
          text: data?.message || t('RESET_PASSWORD_ERROR'),
          type: 'error'
        })
      }
    } catch {
      setMessage({
        text: t('RESET_PASSWORD_ERROR'),
        type: 'error'
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <Overlay open={isLoading} zIndex={4000} backdropOpacity={0.75} />

      <div
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}
      >
        <div
          style={{
            background: '#ededed',
            padding: 20,
            border: '2px solid #107c6b',
            borderRadius: 12,
            boxShadow: '0 12px 28px rgba(0,0,0,0.65)',
            width: 'clamp(320px, 30vw, 420px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <h3 style={{ fontSize: 28, margin: 0, marginBottom: 24 }}>
            {t('RESET_PASSWORD_TITLE')}
          </h3>

          {message && (
            <div
              style={{
                width: '100%',
                boxSizing: 'border-box',
                marginBottom: 12,
                padding: '10px 12px',
                borderRadius: 8,
                border: `1px solid ${
                  message.type === 'success'
                    ? '#1d7c3f'
                    : '#aa2e2e'
                }`,
                background:
                  message.type === 'success'
                    ? '#d8f5df'
                    : '#ffe1e1',
                color:
                  message.type === 'success'
                    ? '#135729'
                    : '#7b1c1c',
                fontSize: 14
              }}
            >
              {message.text}
            </div>
          )}

          <form onSubmit={submit} style={{ width: '100%' }}>

            <input
              autoFocus
              disabled={
                isLoading ||
                tokenValid === false ||
                isSuccess
              }
              type={showPassword ? 'text' : 'password'}
              placeholder={t('RESET_PASSWORD_NEW_PLACEHOLDER')}
              value={password}
              onChange={e => setPassword(e.target.value)}
              onClick={(e) => {
                const rect = e.target.getBoundingClientRect()

                if (e.clientX > rect.right - 40) {
                  setShowPassword(prev => !prev)
                }
              }}
              onMouseMove={(e) => {
                const rect = e.target.getBoundingClientRect()

                e.target.style.cursor =
                  e.clientX > rect.right - 40
                    ? 'pointer'
                    : 'text'
              }}
              style={{
                width: '100%',
                height: 44,
                fontSize: 15,
                padding: '0 18px',
                marginBottom: 12,
                border: '1.5px solid #bfbfbf',
                boxSizing: 'border-box',
                backgroundImage: `url(${
                  showPassword ? hidePwd : showPwd
                })`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 12px center',
                backgroundSize: '20px'
              }}
            />

            <input
              disabled={
                isLoading ||
                tokenValid === false ||
                isSuccess
              }
              type={showConfirm ? 'text' : 'password'}
              placeholder={t('RESET_PASSWORD_CONFIRM_PLACEHOLDER')}
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              onClick={(e) => {
                const rect = e.target.getBoundingClientRect()

                if (e.clientX > rect.right - 40) {
                  setShowConfirm(prev => !prev)
                }
              }}
              onMouseMove={(e) => {
                const rect = e.target.getBoundingClientRect()

                e.target.style.cursor =
                  e.clientX > rect.right - 40
                    ? 'pointer'
                    : 'text'
              }}
              style={{
                width: '100%',
                height: 44,
                fontSize: 15,
                padding: '0 18px',
                marginBottom: 16,
                border: '1.5px solid #bfbfbf',
                boxSizing: 'border-box',
                backgroundImage: `url(${
                  showConfirm ? hidePwd : showPwd
                })`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 12px center',
                backgroundSize: '20px'
              }}
            />

            <div
              style={{
                display: 'flex',
                gap: 10,
                justifyContent: 'center'
              }}
            >
              <button
                type="button"
                disabled={isLoading || isSuccess}
                style={{
                  ...modalSecondaryButtonStyle(
                    pressedBtn,
                    'cancel',
                    hoverBtn === 'cancel'
                  )
                }}
                onMouseEnter={() => setHoverBtn('cancel')}
                onMouseLeave={() => {
                  setHoverBtn(null)
                  setPressedBtn(null)
                }}
                onMouseDown={() => setPressedBtn('cancel')}
                onMouseUp={() => setPressedBtn(null)}
                onClick={closeModal}
              >
                {t('CANCEL')}
              </button>

              <button
                type="submit"
                disabled={
                  isLoading ||
                  tokenValid === false
                }
                style={{
                  ...modalPrimaryButtonStyle(
                    pressedBtn,
                    'reset-submit',
                    hoverBtn === 'reset-submit'
                  )
                }}
                onMouseEnter={() =>
                  setHoverBtn('reset-submit')
                }
                onMouseLeave={() => {
                  setHoverBtn(null)
                  setPressedBtn(null)
                }}
                onMouseDown={() =>
                  setPressedBtn('reset-submit')
                }
                onMouseUp={() => setPressedBtn(null)}
              >
                {isSuccess
                  ? 'OK'
                  : t('CONFIRM')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  )
}

export default ResetPasswordPage